import { Eye, PenLine, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

import SelectionTool, { type SelectionToolProps } from './SelectionTool';
import type { MultiSceneStage } from './MultiSceneViewer';
import type { ActiveContextSelection } from '../utils/viewerSpatialUtils';

interface PeekBehindWallToolProps {
  viewerRef: SelectionToolProps['viewerRef'];
  stages: MultiSceneStage[];
  isOpen: boolean;
  borderCorners?: THREE.Vector3[] | null;
  onClose: () => void;
  onApply: (selection: ActiveContextSelection, stageIndex: number) => void;
  onClear: () => void;
  /** When true, a pre-configured peek is active — stage buttons are enabled without a user-drawn box. */
  preConfiguredActive?: boolean;
  /** Called when the user changes the reveal stage while in pre-configured mode. */
  onPreConfiguredStageChange?: (stageIndex: number) => void;
}

// Projects the 4 projected-face corners (from MultiSceneViewer) to screen space
// each frame and draws an amber viewfinder border around the peek opening.
export function PeekBorder({
  viewerRef,
  corners,
}: {
  viewerRef: SelectionToolProps['viewerRef'];
  corners: THREE.Vector3[];
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const worldCorners = corners;
    const proj = new THREE.Vector3();

    // Face geometry — computed once, corners don't move.
    const faceCenter = new THREE.Vector3()
      .addVectors(worldCorners[0], worldCorners[2])
      .multiplyScalar(0.5);
    const edge1 = new THREE.Vector3().subVectors(worldCorners[1], worldCorners[0]);
    const edge2 = new THREE.Vector3().subVectors(worldCorners[3], worldCorners[0]);
    // Normal points toward the camera that drew the selection (front side of the wall).
    const faceNormal = new THREE.Vector3().crossVectors(edge1, edge2).normalize();

    const clearBorder = (svg: SVGSVGElement) => {
      svg.querySelector<SVGPolygonElement>('#peek-border-poly')?.setAttribute('points', '');
      svg.querySelector<SVGPolygonElement>('#peek-frame-poly')?.setAttribute('points', '');
      svg.querySelectorAll<SVGPolylineElement>('.peek-corner').forEach(el => el.setAttribute('points', ''));
    };

    const tick = () => {
      rafRef.current = requestAnimationFrame(tick);
      const camera  = viewerRef.current?.camera;
      const canvas  = viewerRef.current?.renderer?.domElement;
      const svg     = svgRef.current;
      if (!camera || !canvas || !svg) return;

      const rect = canvas.getBoundingClientRect();
      svg.style.left   = `${rect.left}px`;
      svg.style.top    = `${rect.top}px`;
      svg.style.width  = `${rect.width}px`;
      svg.style.height = `${rect.height}px`;

      // Hide when camera is behind the wall or too far away.
      const faceToCamera = new THREE.Vector3().subVectors(camera.position, faceCenter);
      if (faceToCamera.dot(faceNormal) <= 0 || faceToCamera.length() > 12) {
        clearBorder(svg);
        return;
      }

      const pts = worldCorners.map((wc) => {
        proj.copy(wc).project(camera);
        return {
          x: (proj.x + 1) / 2 * rect.width,
          y: (1 - proj.y) / 2 * rect.height,
          ndcX: proj.x,
          ndcY: proj.y,
          behind: proj.z > 1,
        };
      });

      // Hide when any corner is behind the camera or projects far outside the viewport.
      if (pts.some((p) => p.behind || Math.abs(p.ndcX) > 2 || Math.abs(p.ndcY) > 2)) {
        clearBorder(svg);
        return;
      }

      const pointsStr = pts.map((p) => `${p.x},${p.y}`).join(' ');
      svg.querySelector<SVGPolygonElement>('#peek-border-poly')?.setAttribute('points', pointsStr);
      svg.querySelector<SVGPolygonElement>('#peek-frame-poly')?.setAttribute('points', pointsStr);

      // Corner accent marks — 14px legs at each corner
      const corners = svg.querySelectorAll<SVGPolylineElement>('.peek-corner');
      const LEG = 14;
      pts.forEach((cur, i) => {
        const prev = pts[(i + 3) % 4];
        const next = pts[(i + 1) % 4];
        const dx1 = next.x - cur.x, dy1 = next.y - cur.y;
        const dx2 = prev.x - cur.x, dy2 = prev.y - cur.y;
        const l1 = Math.hypot(dx1, dy1) || 1;
        const l2 = Math.hypot(dx2, dy2) || 1;
        const ax = cur.x + (dx1 / l1) * LEG, ay = cur.y + (dy1 / l1) * LEG;
        const bx = cur.x + (dx2 / l2) * LEG, by = cur.y + (dy2 / l2) * LEG;
        corners[i]?.setAttribute('points', `${ax},${ay} ${cur.x},${cur.y} ${bx},${by}`);
      });
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); };
  }, [corners, viewerRef]);

  return (
    <svg
      ref={svgRef}
      style={{ position: 'fixed', top: 0, left: 0, pointerEvents: 'none', zIndex: 1019 }}
    >
      {/* Solid frame that buries the blurry Gaussian splat transition zone at the border edge */}
      <polygon
        id="peek-frame-poly"
        points=""
        fill="none"
        stroke="rgba(12,12,18,0.92)"
        strokeWidth="16"
        strokeLinejoin="miter"
      />
      {/* Faint fill so the window area is gently highlighted */}
      <polygon
        id="peek-border-poly"
        points=""
        fill="rgba(251,191,36,0.06)"
        stroke="rgba(251,191,36,0.75)"
        strokeWidth="1.5"
        strokeDasharray="6 4"
      />
      {/* Bold corner accents */}
      {[0, 1, 2, 3].map((i) => (
        <polyline
          key={i}
          className="peek-corner"
          points=""
          fill="none"
          stroke="#fbbf24"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}

export default function PeekBehindWallTool({
  viewerRef,
  stages,
  isOpen,
  borderCorners,
  onClose,
  onApply,
  onClear,
  preConfiguredActive = false,
  onPreConfiguredStageChange,
}: PeekBehindWallToolProps) {
  const [selecting, setSelecting]   = useState(false);
  const [selection, setSelection]   = useState<ActiveContextSelection | null>(null);
  const [stageIndex, setStageIndex] = useState(0);

  useEffect(() => {
    if (!isOpen) setSelecting(false);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <SelectionTool
        viewerRef={viewerRef}
        isActive={selecting}
        initialMode="box"
        previewTone="red"
        onClose={() => setSelecting(false)}
        onSelectionChange={() => {}}
        onDone={(nextSelection) => {
          if (!nextSelection.orientedBounds) return;
          setSelection(nextSelection);
          setSelecting(false);
          onApply(nextSelection, stageIndex);
        }}
      />

      {!selecting && (
        <div className="fixed right-[76px] top-3 z-[1002] w-64 overflow-hidden rounded-2xl border border-white/15 bg-black/70 text-white shadow-2xl backdrop-blur-xl">
          <div className="flex items-center justify-between border-b border-white/10 px-3 py-2.5">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Eye size={15} className="text-amber-300" /> Peek Behind Wall
            </div>
            <button onClick={onClose} className="rounded-lg p-1 text-white/50 hover:bg-white/10 hover:text-white">
              <X size={14} />
            </button>
          </div>

          <div className="space-y-3 p-3">
            <button
              onClick={() => setSelecting(true)}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500/20 py-2 text-xs font-semibold text-amber-200 ring-1 ring-amber-400/40 hover:bg-amber-500/30"
            >
              <PenLine size={14} /> {(selection || preConfiguredActive) ? 'Redraw opening' : 'Select wall area'}
            </button>

            <div>
              <div className="mb-1.5 text-[11px] text-white/45">Reveal construction stage</div>
              <div className="flex gap-1">
                {stages.slice(0, -1).map((stage, index) => (
                  <button
                    key={`${stage.url}:${index}`}
                    disabled={!selection && !preConfiguredActive}
                    onClick={() => {
                      setStageIndex(index);
                      if (selection) {
                        onApply(selection, index);
                      } else if (preConfiguredActive) {
                        onPreConfiguredStageChange?.(index);
                      }
                    }}
                    className={`flex-1 rounded-lg py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${
                      stageIndex === index && (selection || preConfiguredActive)
                        ? 'bg-amber-400 text-black'
                        : 'bg-white/10 text-white/65 hover:bg-white/15 hover:text-white'
                    }`}
                  >
                    {stage.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => {
                setSelection(null);
                onClear();
              }}
              className="w-full text-center text-xs text-white/40 hover:text-white/70"
            >
              Clear opening
            </button>
          </div>
        </div>
      )}
    </>
  );
}
